import { Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag } from "lucide-react";
import { mediaUrl, formatPrice } from "@/lib/api";
import ProductPlaceholder from "@/components/ProductPlaceholder";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { toast } from "sonner";

export default function ProductCard({ product }) {
    const { add, buyNow } = useCart();
    const { has, toggle } = useWishlist();
    const navigate = useNavigate();
    const img = product.images?.[0]?.url;

    const onAdd = (e) => {
        e.preventDefault();
        if (!product.in_stock) return;
        add(product, 1);
        toast.success("Added to cart");
    };

    const onBuy = (e) => {
        e.preventDefault();
        if (!product.in_stock) return;
        buyNow(product, 1);
        navigate("/checkout?mode=buynow");
    };

    const onWish = async (e) => {
        e.preventDefault();
        const added = await toggle(product.id);
        toast.success(added ? "Added to wishlist" : "Removed from wishlist");
    };

    return (
        <div className="group" data-testid={`product-card-${product.slug}`}>
            <Link to={`/products/${product.slug}`} className="block relative overflow-hidden bg-cream-soft aspect-[4/5]">
                {img ? (
                    <img src={mediaUrl(img)} alt={product.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                    <ProductPlaceholder className="w-full h-full" />
                )}
                {product.discount_pct > 0 && (
                    <span className="absolute top-3 left-3 bg-ink text-cream text-[11px] px-2.5 py-1 tracking-wider">{product.discount_pct}% OFF</span>
                )}
                {!product.in_stock && (
                    <span className="absolute top-3 right-3 bg-white/90 text-ink text-[11px] px-2.5 py-1 tracking-wider">Out of Stock</span>
                )}
                <button
                    data-testid={`wishlist-toggle-${product.slug}`}
                    onClick={onWish}
                    className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Add to wishlist"
                >
                    <Heart className={`h-4 w-4 ${has(product.id) ? "fill-gold text-gold" : "text-ink"}`} strokeWidth={1.5} />
                </button>
            </Link>

            <div className="pt-4">
                <Link to={`/products/${product.slug}`}>
                    <h3 className="text-[15px] font-medium leading-snug hover:text-gold transition-colors">{product.name}</h3>
                </Link>
                <div className="flex items-baseline gap-2 mt-1.5">
                    <span className="text-[15px] font-semibold">{formatPrice(product.price)}</span>
                    {product.mrp > product.price && <span className="text-xs text-muted-foreground line-through">{formatPrice(product.mrp)}</span>}
                </div>
                <div className="flex items-center gap-2 mt-3">
                    <button
                        data-testid={`add-to-cart-${product.slug}`}
                        onClick={onAdd}
                        disabled={!product.in_stock}
                        className="flex-1 flex items-center justify-center gap-1.5 border border-ink text-ink text-xs uppercase tracking-[0.15em] py-2.5 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ShoppingBag className="h-3.5 w-3.5" strokeWidth={1.5} /> Add
                    </button>
                    <button
                        data-testid={`buy-now-${product.slug}`}
                        onClick={onBuy}
                        disabled={!product.in_stock}
                        className="flex-1 bg-gold text-white text-xs uppercase tracking-[0.15em] py-2.5 hover:bg-gold-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Buy Now
                    </button>
                </div>
            </div>
        </div>
    );
}
